import React from 'react';
import type * as TF from 'type-fest';
import TablerIconAlertTriangleFilled from '~icons/tabler/alert-triangle-filled';
import TablerIconCircleCheckFilled from '~icons/tabler/circle-check-filled';
import TablerIconInfoCircle from '~icons/tabler/info-circle';
import TablerIconInfoCircleFilled from '~icons/tabler/info-circle-filled';
import TablerIconX from '~icons/tabler/x';
import TablerIconXboxXFilled from '~icons/tabler/xbox-x-filled';

import Box from '#/ui/components/box';
import type { OverridableStringUnion, OverrideProps } from '#/ui/utils/types';
import { cn } from '#/utils/classnames';
import IconButton from './icon-button';

export type AlertPropsColorOverrides = TF.EmptyObject;

export type AlertPropsVariantOverrides = TF.EmptyObject;

export interface AlertOwnProps {
  /**
   * The main content of the alert.
   */
  children?: React.ReactNode;
  /**
   * Override or extend the styles applied to the component.
   */
  className?: string;
  /**
   * The severity of the alert. This defines the color and icon used.
   * @default 'info'
   */
  severity?: OverridableStringUnion<
    'error' | 'info' | 'success' | 'warning' | 'neutral',
    AlertPropsColorOverrides
  >;
  /**
   * The variant to use.
   * @default 'standard'
   */
  variant?: OverridableStringUnion<'standard' | 'filled' | 'outlined', AlertPropsVariantOverrides>;
  /**
   * The title of the alert.
   */
  title?: React.ReactNode;
  /**
   * Callback fired when the alert is closed.
   */
  onClose?: () => void;
  /**
   * If true, the alert will show an icon before the content.
   * @default true
   */
  icon?: React.ReactNode | boolean;
}

export type AlertTypeMap<
  AdditionalProps = TF.EmptyObject,
  RootComponent extends React.ElementType = 'div',
> = {
  props: AdditionalProps & AlertOwnProps;
  defaultComponent: RootComponent;
};

export type AlertProps<
  RootComponent extends React.ElementType = AlertTypeMap['defaultComponent'],
  AdditionalProps = TF.EmptyObject,
  // @ts-expect-error
> = OverrideProps<AlertTypeMap<AdditionalProps, RootComponent>, RootComponent> & {
  component?: React.ElementType;
};

function Alert<C extends React.ElementType = 'div'>(
  {
    component = 'div',
    children,
    className,
    severity = 'info',
    variant = 'standard',
    title,
    onClose,
    icon = true,
    ...props
  }: AlertProps<C>,
  ref?: React.ForwardedRef<C>
) {
  const Icon = React.useMemo(() => iconMap[severity], [severity]);
  return (
    <Box
      {...props}
      component={component}
      ref={ref}
      role='alert'
      className={cn('gooey-alert', 'd-alert', variantMap[variant][severity], className)}
    >
      {Icon && <Icon className='size-6 flex-shrink-0' />}

      <div className='flex-1'>
        {title && <div className='font-bold mt-0 mb-1 text-xl'>{title}</div>}
        {children}
      </div>

      {onClose && (
        <IconButton shape='circle' size='small' onClick={onClose}>
          <TablerIconX className='size-5' />
        </IconButton>
      )}
    </Box>
  );
}

Alert.displayName = 'Alert';

export default Alert;

/*
|------------------
| Internals
|------------------
*/

const variantMap = {
  standard: {
    error: 'd-alert-error d-alert-soft',
    warning: 'd-alert-warning d-alert-soft',
    info: 'd-alert-info d-alert-soft',
    success: 'd-alert-success d-alert-soft',
    neutral: 'd-alert-soft',
  },
  filled: {
    error: 'd-alert-error',
    warning: 'd-alert-warning',
    info: 'd-alert-info',
    success: 'd-alert-success',
    neutral: '',
  },
  outlined: {
    error: 'd-alert-error d-alert-outline',
    warning: 'd-alert-warning d-alert-outline',
    info: 'd-alert-info d-alert-outline',
    success: 'd-alert-success d-alert-outline',
    neutral: 'd-alert-outline',
  },
} as const;

const iconMap = {
  error: TablerIconXboxXFilled,
  warning: TablerIconAlertTriangleFilled,
  info: TablerIconInfoCircleFilled,
  success: TablerIconCircleCheckFilled,
  neutral: TablerIconInfoCircle,
} as const;
